

export const dynamic = 'force-dynamic';

import VendorReportsLayout, { useVendorPeriod, type Period } from "../VendorReportsLayout";
import { reportsApi } from "@/lib/api";
import {
  Card, ExportButtons, Kpi, KpiGrid, Note, ReportBody, TableScroll, money, th, td, useCanSeeMoney, useReport, type ExportSpec,
} from "../../ReportKit";

interface VendorRow {
  id: number;
  name: string;
  vendor_type: string | null;
  phone: string | null;
  events: number;
  cancelled_events: number;
  billed?: number;
  paid?: number;
  charged?: number;
  commission?: number;
  owed_now?: number;
  advance_now?: number;
}

interface PerformanceReport {
  can_view_money: boolean;
  summary: { vendors: number; events: number; assignments: number; billed?: number; paid?: number; owed_now?: number; commission?: number };
  vendors: VendorRow[];
}

function performanceNote(showMoney: boolean): string {
  return "Events = events (not cancelled) starting in the period that the vendor worked on, through a vendor line or a service given to them."
    + (showMoney ? " Cost of work is what that work costs us (a cancelled event only what was already paid); “Owed today” is the vendor’s whole balance, the same as on the Vendors page." : "");
}

/** Excel / Print: the KPIs and the vendor table, money columns only for roles that see them. */
function buildExport(d: PerformanceReport, showMoney: boolean, period: Period): ExportSpec {
  const s = d.summary;
  return {
    title: "Vendor Performance",
    fileName: "vendor-performance",
    period,
    summary: [
      { label: "Vendors", value: s.vendors, type: "number" },
      { label: "Events served", value: s.events, type: "number" },
      { label: "Vendor bookings", value: s.assignments, type: "number" },
      ...(showMoney
        ? [
          { label: "Cost of their work", value: s.billed, type: "money" as const },
          { label: "Paid towards it", value: s.paid, type: "money" as const },
          { label: "Owed to vendors today", value: s.owed_now, type: "money" as const },
        ]
        : []),
    ],
    tables: [{
      title: "Vendors",
      columns: [
        { label: "Vendor" }, { label: "Category" }, { label: "Events", type: "number" }, { label: "Cancelled", type: "number" },
        ...(showMoney
          ? [
            { label: "Cost of work", type: "money" as const },
            { label: "Paid", type: "money" as const },
            { label: "Charged to clients", type: "money" as const },
            { label: "Owed today", type: "money" as const },
            { label: "Advance", type: "money" as const },
          ]
          : []),
      ],
      rows: d.vendors.map((v) => [
        v.name, v.vendor_type, v.events, v.cancelled_events,
        ...(showMoney
          ? [v.billed, v.paid, v.charged, (v.owed_now ?? 0) > 0 ? v.owed_now : null, (v.advance_now ?? 0) > 0 ? v.advance_now : null]
          : []),
      ]),
      totals: showMoney ? ["Total", null, s.assignments, null, s.billed, s.paid, null, s.owed_now, null] : undefined,
    }],
    notes: [performanceNote(showMoney)],
  };
}

export default function VendorPerformancePage() {
  const [period, setPeriod] = useVendorPeriod();
  const canSeeMoney = useCanSeeMoney();

  const state = useReport<PerformanceReport>(
    async () => (await reportsApi.vendorPerformance({ from_date: period.from, to_date: period.to })).data,
    [period.from, period.to],
  );

  return (
    <VendorReportsLayout
      period={period}
      onPeriodChange={setPeriod}
      actions={
        <ExportButtons
          build={() => (state.data ? buildExport(state.data, canSeeMoney && state.data.can_view_money, period) : null)}
          disabled={state.loading || !state.data?.vendors.length}
        />
      }
    >
      <ReportBody state={state}>
        {(d) => {
          const showMoney = canSeeMoney && d.can_view_money;
          const s = d.summary;
          return (
            <>
              <KpiGrid>
                <Kpi label="Vendors" value={s.vendors} sub="with work in the period" tone="stone" />
                <Kpi label="Events served" value={s.events} sub={`${s.assignments} vendor bookings`} tone="emerald" />
                {showMoney && <Kpi label="Cost of their work" value={money(s.billed)} sub={`Paid ${money(s.paid)} towards it`} tone="amber" />}
                {showMoney && <Kpi label="Owed to vendors today" value={money(s.owed_now)} sub="all events, from the vendor ledger" tone={(s.owed_now ?? 0) > 0 ? "red" : "stone"} />}
              </KpiGrid>

              <Card title="Vendors">
                {d.vendors.length === 0 ? (
                  <p className="py-8 text-center text-sm text-stone-400">No vendor work on events in this period.</p>
                ) : (
                  <TableScroll minWidth={showMoney ? 860 : 420}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-stone-100">
                          <th className={`${th} text-left`}>Vendor</th>
                          <th className={`${th} text-right`}>Events</th>
                          <th className={`${th} text-right`}>Cancelled</th>
                          {showMoney && <th className={`${th} text-right`}>Cost of work</th>}
                          {showMoney && <th className={`${th} text-right`}>Paid</th>}
                          {showMoney && <th className={`${th} text-right`}>Charged to clients</th>}
                          {showMoney && <th className={`${th} text-right`}>Owed today</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-50">
                        {d.vendors.map((v) => (
                          <tr key={v.id} className="hover:bg-stone-50/50">
                            <td className={td}>
                              <div className="font-semibold text-stone-800">{v.name}</div>
                              {v.vendor_type && <div className="text-xs text-stone-400">{v.vendor_type}</div>}
                            </td>
                            <td className={`${td} text-right font-semibold text-stone-700`}>{v.events}</td>
                            <td className={`${td} text-right ${v.cancelled_events ? "text-rose-600" : "text-stone-400"}`}>{v.cancelled_events || "—"}</td>
                            {showMoney && <td className={`${td} text-right tabular-nums text-stone-800`}>{money(v.billed)}</td>}
                            {showMoney && <td className={`${td} text-right tabular-nums text-emerald-700`}>{money(v.paid)}</td>}
                            {showMoney && <td className={`${td} text-right tabular-nums text-stone-500`}>{money(v.charged)}</td>}
                            {showMoney && (
                              <td className={`${td} text-right tabular-nums font-semibold`}>
                                {(v.owed_now ?? 0) > 0 ? <span className="text-amber-700">{money(v.owed_now)}</span>
                                  : (v.advance_now ?? 0) > 0 ? <span className="text-emerald-700">Advance {money(v.advance_now)}</span>
                                  : <span className="text-stone-400">—</span>}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                      {showMoney && (
                        <tfoot>
                          <tr className="border-t-2 border-stone-200 bg-stone-50 font-bold">
                            <td className={td}>Total</td>
                            <td className={`${td} text-right`}>{s.assignments}</td>
                            <td className={td} />
                            <td className={`${td} text-right tabular-nums`}>{money(s.billed)}</td>
                            <td className={`${td} text-right tabular-nums`}>{money(s.paid)}</td>
                            <td className={td} />
                            <td className={`${td} text-right tabular-nums`}>{money(s.owed_now)}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </TableScroll>
                )}
              </Card>

              <Note>{performanceNote(showMoney)}</Note>
            </>
          );
        }}
      </ReportBody>
    </VendorReportsLayout>
  );
}
